import { breakpoints, Heading, Inline, Stack } from '@etg/wings';
import { useProperty, useSearch, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { Notification, variants } from '@eti/components';
import type { Dispatch, SetStateAction } from 'react';
import ShareTrip from '../../../../../../widgets/share-trip/ShareTrip';
import { SaveTrip } from '../saveTrip/SaveTrip';
import type {
  RebookingTripBoundsType,
  RebookingTripTypeType,
} from '../../../../../change-trip/models';
import type { BoundsType } from '../../../../models';
import type { TripType as CustomTripType } from '../model';
import { isTripWithDifferentOriginOrDestination } from '../../../../../../widgets/trip-details/utils/originDestinationUtil';
import { routes } from '../../../../../../constants/routesAndApisConstants';
import { getSelfTransferPlaces } from '../../utils/trip';
import ItineraryDetailsLocationTime from './ItineraryDetailsLocationTime';
import { getFinalSegments } from './utils';

const headlineStyles = css`
  justify-content: space-between;
`;

const saveShareTripStyles = css`
  gap: 14px;
`;

interface ItineraryContainerProps {
  bounds: BoundsType | RebookingTripBoundsType;
  isSponsoredTrip?: boolean;
  isVI?: boolean;
  priceDisplayText: string;
  setIsItineraryDrawerSelfTransferModalOpen?: Dispatch<SetStateAction<boolean>>;
  shareableUrl?: string;
  shouldShowSaveTrip?: boolean;
  shouldShowShareTrip?: boolean;
  totalPrice?: string;
  type: CustomTripType | RebookingTripTypeType;
}

export const ItineraryInfo = ({
  bounds,
  isSponsoredTrip,
  isVI,
  priceDisplayText,
  setIsItineraryDrawerSelfTransferModalOpen,
  shareableUrl,
  shouldShowSaveTrip = false,
  shouldShowShareTrip = false,
  totalPrice,
  type,
}: ItineraryContainerProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { currentSearchData } = useSearch();
  const segments = getFinalSegments(bounds);
  const isChangeTripPage = window.location.pathname.includes(routes.CHANGE_TRIP);
  const isDifferentAirportNotificationEnabled = p('Result.DifferentAirportNotification.Enabled');
  const tripHasDifferentOriginOrDestination =
    !isChangeTripPage &&
    isTripWithDifferentOriginOrDestination(currentSearchData?.routes ?? [], bounds);
  const shouldShowDifferentAirportBadge =
    tripHasDifferentOriginOrDestination && isDifferentAirportNotificationEnabled;

  return (
    <Stack data-testid="itinerary-info-wrapper">
      <Inline className={headlineStyles}>
        <Heading level={3}>{t('Result.Page.Drawer.Itinerary.Title')}</Heading>
        <Inline className={saveShareTripStyles}>
          {shouldShowSaveTrip && Boolean(totalPrice) && (
            <SaveTrip
              isSponsoredTrip={isSponsoredTrip}
              isVI={isVI}
              priceDisplayText={priceDisplayText}
              totalPrice={totalPrice!!}
              type={type}
            />
          )}
          {shouldShowShareTrip && shareableUrl && (
            <ShareTrip
              bounds={bounds}
              breakpoint={breakpoints._0}
              priceDisplayText={priceDisplayText}
              shareableUrl={shareableUrl}
              totalPrice={totalPrice?.toString()}
              type={type}
            />
          )}
        </Inline>
      </Inline>
      <div>
        {segments.map((segment, index) => (
          <ItineraryDetailsLocationTime
            key={segment.segmentId}
            segment={segment}
            selfTransferPlaces={getSelfTransferPlaces(bounds, index)}
            setIsItineraryDrawerSelfTransferModalOpen={setIsItineraryDrawerSelfTransferModalOpen}
            tripType={type}
          />
        ))}
      </div>
      {shouldShowDifferentAirportBadge && (
        <Notification
          dataTestId="nearby-airports-drawer-notification"
          title={t('Result.TripDetails.DifferentAirport.Header')}
          variant={variants.INFORMATION}
        >
          {t('Result.TripDetails.DifferentAirport.Description')}
        </Notification>
      )}
    </Stack>
  );
};
